import { Injectable, inject } from '@angular/core';

import { Observable, switchMap, of } from 'rxjs';

import { Logger } from '@sondermind/datadog-logging';

import { IPaymentMethodResult, IPaymentMethod } from '../models/payment-method.model';
import { PaymentMethodHttpService } from '../services/payment-method.httpservice';

/**
 * Store for creating payment methods via the SetupIntent flow.
 *
 * For fetching, updating, and deleting payment methods, use CreditCardStore instead.
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentMethodStore {
  private readonly paymentMethodHttpService = inject(PaymentMethodHttpService);
  private readonly logger = inject(Logger);

  /**
   * Create and save a payment method using the SetupIntent flow.
   * This is the recommended way to save payment methods as it validates the card with Stripe
   * and handles 3D Secure authentication before saving.
   *
   * @param paymentMethodTypes - Payment types to support (e.g., ['card'])
   * @param nickname - Display name for the payment method
   * @param accountSetupContext - Optional context string
   * @param confirmFn - Callback that takes client_secret and returns Observable<IPaymentMethodResult>
   *   The result is a discriminated union with type-specific details (brand, last4, etc.)
   * @param usage - 'off_session' for recurring payments (default), 'on_session' for user-present flows
   */
  createPaymentMethod(
    paymentMethodTypes: string[],
    nickname: string,
    accountSetupContext: string | null,
    confirmFn: (clientSecret: string) => Observable<IPaymentMethodResult>,
    usage: 'off_session' | 'on_session' = 'off_session'
  ): Observable<IPaymentMethod | null> {
    return this.paymentMethodHttpService.createSetupIntent(paymentMethodTypes, usage).pipe(
      switchMap((setupIntent) => {
        if (!setupIntent?.client_secret) {
          this.logger.error('Failed to create SetupIntent', { setupIntent });
          return of(null);
        }

        // Call component's confirm function with client_secret
        return confirmFn(setupIntent.client_secret);
      }),
      switchMap((result) => {
        if (!result?.paymentMethodId) {
          this.logger.error('Stripe confirmation did not return paymentMethodId', { result });
          return of(null);
        }

        // Save validated payment method to backend
        return this.paymentMethodHttpService.create(
          result.paymentMethodId,
          nickname,
          accountSetupContext,
          result.type
        );
      })
    );
  }
}
